import { cn } from "@/lib/utils";

import { portraitImageProps } from "./leadership-images";
import type { PortraitFrame } from "./leadership-images";

interface LeadershipPortraitProps {
  src: string;
  alt: string;
  mobile: PortraitFrame;
  tablet: PortraitFrame;
  desktop: PortraitFrame;
  className?: string;
}

function frameStyle(
  frame: PortraitFrame,
  options?: { centerInFrame?: boolean }
): React.CSSProperties {
  if (options?.centerInFrame) {
    return {
      width: frame.width,
      height: frame.height,
      top: frame.top,
      left: "50%",
      transform: "translateX(-50%)",
    };
  }

  return {
    width: frame.width,
    height: frame.height,
    left: frame.left,
    top: frame.top,
  };
}

/**
 * Absolutely positioned portrait crop inside an overflow-hidden frame.
 * Mobile, tablet, and desktop frames from Figma 5890-27280 / 27279 / 27281.
 */
export function LeadershipPortrait({
  src,
  alt,
  mobile,
  tablet,
  desktop,
  className,
}: LeadershipPortraitProps) {
  return (
    <>
      {/* Mobile — full-width 220px card frame; center crop horizontally because w-full varies by viewport */}
      <div
        className={cn("absolute md:hidden", className)}
        style={frameStyle(mobile, { centerInFrame: true })}
      >
        <PortraitImage
          src={src}
          alt={alt}
          displayWidth={mobile.width}
          imageLeft={mobile.imageLeft}
          imageTop={mobile.imageTop}
          lazy
        />
      </div>
      {/* Tablet — 160×160 card frame */}
      <div
        className={cn("absolute hidden md:block lg:hidden", className)}
        style={frameStyle(tablet)}
      >
        <PortraitImage
          src={src}
          alt={alt}
          displayWidth={tablet.width}
          imageLeft={tablet.imageLeft}
          imageTop={tablet.imageTop}
          lazy
        />
      </div>
      {/* Desktop — 358px-tall card frame; center horizontally because w-full varies by column */}
      <div
        className={cn("absolute hidden lg:block", className)}
        style={frameStyle(desktop, { centerInFrame: true })}
      >
        <PortraitImage
          src={src}
          alt={alt}
          displayWidth={desktop.width}
          imageLeft={desktop.imageLeft}
          imageTop={desktop.imageTop}
          lazy
        />
      </div>
    </>
  );
}

interface LeadershipModalPortraitProps {
  src: string;
  alt: string;
  frame: PortraitFrame;
}

/** Single-frame portrait for the modal image area (Figma 6436-28946). */
export function LeadershipModalPortrait({
  src,
  alt,
  frame,
}: LeadershipModalPortraitProps) {
  return (
    <div
      className="absolute overflow-hidden"
      style={frameStyle(frame, { centerInFrame: true })}
    >
      <PortraitImage
        src={src}
        alt={alt}
        displayWidth={frame.width}
        imageLeft={frame.imageLeft}
        imageTop={frame.imageTop}
      />
    </div>
  );
}

function PortraitImage({
  src,
  alt,
  displayWidth,
  imageLeft,
  imageTop,
  lazy = false,
}: {
  src: string;
  alt: string;
  /** CSS width of the frame this portrait fills, for CDN resizing. */
  displayWidth: number;
  imageLeft?: string;
  imageTop?: string;
  lazy?: boolean;
}) {
  const imageProps = portraitImageProps(src, displayWidth);

  if (imageLeft || imageTop) {
    return (
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          {...imageProps}
          alt={alt}
          className="absolute size-full max-w-none object-cover"
          style={{ left: imageLeft, top: imageTop }}
          loading={lazy ? "lazy" : undefined}
          decoding="async"
        />
      </div>
    );
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      {...imageProps}
      alt={alt}
      className="size-full max-w-none object-cover"
      loading={lazy ? "lazy" : undefined}
      decoding="async"
    />
  );
}
